import React, { useRef } from 'react'
import { Box, Button, Collapse, Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Cancel, CheckCircle, Sync } from '@mxenabled/mx-icons'
import { Text } from '@mxenabled/mxui'

export type ObsolesceBannerState =
  | 'refresh_available'
  | 'refreshing'
  | 'updated'
  | 'not_refreshable'
  | 'hidden'

export interface ObsolesceBannerCopy {
  new_information_available: string
  updating: string
  update: string
  insight_updated: string
  insight_no_longer_applicable: string
  dismiss: string
}

export interface ObsolesceBannerProps {
  state: ObsolesceBannerState
  onRefresh: () => void
  onDismiss: () => void
  copy: ObsolesceBannerCopy
}

const BANNER_HEIGHT = 50

const bannerButtonSx = {
  fontSize: 11,
  minWidth: 'auto',
  padding: '4px 8px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
} as const

/**
 * Presentational obsolescence banner. State transitions are announced to
 * screen readers by the host via a global aria-live region (Pulse's
 * useObsoleteManager dispatches updateAriaLive on each transition, matching
 * the legacy ObsoleteManager). A component-level aria-live is intentionally
 * omitted: it would double-announce in Pulse and, because the banner unmounts
 * via <Collapse unmountOnExit>, would be a less reliable live region than a
 * persistent global one.
 */
export const ObsolesceBanner: React.FC<ObsolesceBannerProps> = ({
  state,
  onRefresh,
  onDismiss,
  copy,
}) => {
  const theme = useTheme()

  const isRefreshAvailable = state === 'refresh_available'
  const isRefreshing = state === 'refreshing'
  const isUpdated = state === 'updated'
  const isNotRefreshable = state === 'not_refreshable'
  const isVisible = state !== 'hidden'

  const getBannerConfig = () => {
    if (isRefreshAvailable || isRefreshing) {
      return {
        backgroundColor: theme.palette.success?.lighter,
        iconColor: theme.palette.success?.dark,
        textColor: theme.palette.success?.dark,
        icon: (
          <Sync
            sx={{
              ...(isRefreshing && {
                animation: 'spin 1.6s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }),
            }}
          />
        ),
        message: isRefreshing ? copy.updating : copy.new_information_available,
        button: (
          <Button
            onClick={onRefresh}
            disabled={isRefreshing}
            variant="text"
            size="small"
            aria-label={copy.update}
            sx={{
              ...bannerButtonSx,
              color: isRefreshing ? theme.palette.text?.secondary : theme.palette.success?.dark,
            }}
          >
            <Text bold={true} variant="body2">
              {copy.update}
            </Text>
          </Button>
        ),
      }
    }

    if (isUpdated) {
      return {
        backgroundColor: theme.palette.success?.lighter,
        iconColor: theme.palette.success?.dark,
        textColor: theme.palette.success?.dark,
        icon: <CheckCircle />,
        message: copy.insight_updated,
        button: null,
      }
    }

    if (isNotRefreshable) {
      return {
        backgroundColor: theme.palette.neutral?.light,
        iconColor: theme.palette.neutral?.dark,
        textColor: theme.palette.neutral?.dark,
        icon: <Cancel filled={true} />,
        message: copy.insight_no_longer_applicable,
        button: (
          <Button
            onClick={onDismiss}
            variant="text"
            size="small"
            aria-label={copy.dismiss}
            sx={{
              ...bannerButtonSx,
              color: theme.palette.neutral?.dark,
            }}
          >
            <Text bold={true} variant="body2">
              {copy.dismiss}
            </Text>
          </Button>
        ),
      }
    }

    return null
  }

  const config = getBannerConfig()

  // Keep the last visible config so the banner can animate out on 'hidden'
  // instead of unmounting instantly (Collapse needs content while in={false}).
  const lastConfig = useRef(config)
  if (config) lastConfig.current = config
  const renderedConfig = config ?? lastConfig.current

  if (!renderedConfig) {
    return null
  }

  return (
    <Collapse in={isVisible} unmountOnExit>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: renderedConfig.backgroundColor,
          borderRadius: '8px',
          display: 'flex',
          height: BANNER_HEIGHT,
          justifyContent: 'space-between',
          pb: 14,
          position: 'relative',
          pt: 8,
          px: 12,
          top: 10,
        }}
      >
        <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 4 }}>
          <Box
            sx={{
              color: renderedConfig.iconColor,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {renderedConfig.icon}
          </Box>
          <Text
            variant="subtitle2"
            sx={{
              color: renderedConfig.textColor,
            }}
          >
            {renderedConfig.message}
          </Text>
        </Stack>
        {renderedConfig.button}
      </Box>
    </Collapse>
  )
}
